import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { motion, AnimatePresence, useReducedMotion, type Variants, type Transition } from 'framer-motion';
import { createJob, createPremiumJob, detectFaces } from '../api/endpoints';
import { isCreditError } from '../api/config';
import { useUser } from '../contexts/UserContext';
import { navigateTo } from '../utils/navigation';
import { getCroppedImageBlob } from '../utils/cropImage';
import CameraCapture from '../components/CameraCapture';
import PhotoConsentGate from '../components/PhotoConsentGate';
import ModalShell from '../components/ModalShell';
import type { ScreenProps } from '../types/screen';
import { getErrorMessage } from '../utils/errors';

// Long side of the image sent to the face check. The face service detects at
// 192 px, so 384 keeps a 2x margin at a quarter of the upload's pixels. The
// mobile app sends the same size.
const FACE_CHECK_SIZE = 384;

export interface FaceScanInput {
  /** Force the premium pipeline regardless of plan. Used by the ai_premium_face_scan tool. */
  forcePremium?: boolean;
}

export interface FaceScanResult {
  jobId: string;
  kind: 'base' | 'premium';
}

const FaceScanScreen: React.FC<ScreenProps<FaceScanInput, FaceScanResult>> = ({
  mode,
  input,
  onComplete,
  onCancel,
}) => {
  const forcePremium = !!input?.forcePremium;
  const [step, setStep] = useState<1 | 2>(1);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [faceStatus, setFaceStatus] = useState<string>('');
  const [checking, setChecking] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [photoConsentGranted, setPhotoConsentGranted] = useState<boolean>(false);

  const { credits, costFor, canAfford, refreshCredits } = useUser();

  const featureKey = forcePremium ? 'premium_face' : 'basic_skin_scan';
  const cost = costFor(featureKey);
  const total = credits?.total ?? null;
  const outOfQuota = !canAfford(featureKey);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const prefersReduced = useReducedMotion();
  const spring: Transition = prefersReduced
    ? { duration: 0 }
    : { type: 'spring', stiffness: 380, damping: 34, mass: 0.95 };
  const quick: Transition = prefersReduced ? { duration: 0 } : { duration: 0.2 };

  const headerIn: Variants = { hidden: { opacity: 0, y: 6 }, visible: { opacity: 1, y: 0, transition: spring } };
  const cardVariants: Variants = {
    hidden: { opacity: 0, y: 12, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1, transition: spring },
    exit: { opacity: 0, y: -8, transition: quick },
  };
  const innerStagger: Variants = {
    hidden: {},
    visible: { transition: { staggerChildren: prefersReduced ? 0 : 0.06, delayChildren: prefersReduced ? 0 : 0.05 } },
  };
  const innerItem: Variants = { hidden: { opacity: 0, y: 6 }, visible: { opacity: 1, y: 0, transition: spring } };

  const helperText = useMemo(() => {
    if (outOfQuota) return 'Insufficient credits — top up or wait for your monthly reset.';
    if (step === 1) return 'Step 1 — Add a photo: upload from your device or take a new one.';
    if (checking) return 'Step 2 — Move or resize the square so your face is centered. Checking…';
    if (faceStatus === 'Good') return 'Looks good. You can upload when ready.';
    if (faceStatus.startsWith('Bad')) return 'Center your face in the frame.';
    if (faceStatus) return faceStatus;
    return 'Step 2 — Center your face in the square and adjust zoom if needed.';
  }, [step, checking, faceStatus, outOfQuota]);

  const handleBlob = (blob: Blob) => {
    if (outOfQuota) return;
    setImageSrc(URL.createObjectURL(blob));
    setFaceStatus('');
    setStep(2);
  };

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  useEffect(() => {
    if (!imageSrc || !croppedAreaPixels) return;
    const timer = setTimeout(() => {
      void (async () => {
        setChecking(true);
        try {
          // The face check only needs a small copy; the upload keeps 768 px.
          const blob = await getCroppedImageBlob(imageSrc, croppedAreaPixels, {
            size: FACE_CHECK_SIZE,
          });
          const res = await detectFaces(blob);
          setFaceStatus(res.response);
        } catch (error) {
          console.error('[Face Check] Error:', error);
          setFaceStatus('Error detecting face');
        } finally {
          setChecking(false);
        }
      })();
    }, 1000);
    return () => { clearTimeout(timer); };
  }, [imageSrc, croppedAreaPixels]);

  const handleUploadJob = async () => {
    if (outOfQuota) return;
    if (faceStatus !== 'Good' || !imageSrc || !croppedAreaPixels) return;
    setUploading(true);
    try {
      const blob = await getCroppedImageBlob(imageSrc, croppedAreaPixels);
      let jobId: string;
      let kind: 'base' | 'premium';
      if (forcePremium) {
        const res = await createPremiumJob(blob);
        jobId = res.job_id;
        kind = 'premium';
      } else {
        const res = await createJob(blob);
        jobId = res.job_id;
        kind = 'base';
      }

      void refreshCredits();
      if (mode === 'modal') {
        setUploading(false);
        onComplete?.({ jobId, kind });
      } else {
        const path = kind === 'premium' ? `/premium-analysis/${jobId}/pending` : `/analyses/${jobId}/pending`;
        navigateTo(path);
      }
    } catch (error: unknown) {
      if (isCreditError(error)) {
        setUploadError(error.message);
        void refreshCredits();
      } else {
        console.error('[Upload Job] Error:', error);
        setUploadError(getErrorMessage(error, 'Upload failed — please check your connection and try again.'));
      }
      setUploading(false);
    }
  };

  const openPicker = () => fileInputRef.current?.click();
  const onPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleBlob(file);
    e.currentTarget.value = '';
  };
  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files.item(0);
    if (file === null) return;
    handleBlob(file);
  };

  const StatusChip = () => {
    if (!faceStatus) return null;
    const isGood = faceStatus === 'Good';
    return (
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        className={`pill ${checking ? '' : isGood ? 'pill--teal' : 'pill--coral'}`}
      >
        <span className={`dot${checking ? ' dot--coral animate-pulse' : isGood ? '' : ' dot--coral'}`} />
        <span>{checking ? 'Checking…' : faceStatus}</span>
      </motion.div>
    );
  };

  const ScannerFrame = () => (
    <div className="absolute inset-0 pointer-events-none z-10">
      <div className="absolute top-4 left-4 w-8 h-8 rounded-tl-lg" style={{ borderTop: '2px solid var(--c-kivo-teal)', borderLeft: '2px solid var(--c-kivo-teal)', opacity: .5 }} />
      <div className="absolute top-4 right-4 w-8 h-8 rounded-tr-lg" style={{ borderTop: '2px solid var(--c-kivo-teal)', borderRight: '2px solid var(--c-kivo-teal)', opacity: .5 }} />
      <div className="absolute bottom-4 left-4 w-8 h-8 rounded-bl-lg" style={{ borderBottom: '2px solid var(--c-kivo-teal)', borderLeft: '2px solid var(--c-kivo-teal)', opacity: .5 }} />
      <div className="absolute bottom-4 right-4 w-8 h-8 rounded-br-lg" style={{ borderBottom: '2px solid var(--c-kivo-teal)', borderRight: '2px solid var(--c-kivo-teal)', opacity: .5 }} />
      {checking && (
        <motion.div
          initial={{ top: '10%' }}
          animate={{ top: '90%' }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          className="absolute left-4 right-4 h-px"
          style={{ background: 'linear-gradient(to right, transparent, var(--c-kivo-teal), transparent)' }}
        />
      )}
    </div>
  );

  const PlusIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );

  const handleConsentAccepted = useCallback(() => {
    setPhotoConsentGranted(true);
  }, []);

  const inner = (
    <>
      <PhotoConsentGate onAccepted={handleConsentAccepted} />

      {photoConsentGranted && (
        <div className="w-full max-w-full overflow-x-hidden stack-5">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={onPicked}
            className="hidden"
            aria-hidden="true"
          />

          {/* Header */}
          <motion.div
            variants={headerIn}
            initial="hidden"
            animate="visible"
            className="flex items-start justify-between gap-4"
          >
            <div className="flex-1">
              {mode === 'page' && (
                <h1 className="t-h1" style={{ marginBottom: 'var(--s-3)', marginTop: 0 }}>
                  {forcePremium ? <><em className="t-italic">Premium</em> Analysis</> : 'New Analysis'}
                </h1>
              )}
              <p className="t-small t-mute" style={{ maxWidth: '52ch', margin: 0 }}>{helperText}</p>
            </div>

            <div className="hidden sm:flex items-center" style={{ gap: 'var(--s-3)' }}>
              {step === 2 && <StatusChip />}

              {cost !== null && (
                <span
                  className={`pill ${outOfQuota ? 'pill--coral' : 'pill--teal'}`}
                  title={outOfQuota ? 'Not enough credits' : `${cost} credits${total !== null ? ` · ${total} left` : ''}`}
                >
                  <span className={`dot${outOfQuota ? ' dot--coral' : ''}`} />
                  {outOfQuota ? `Need ${cost} credits` : `${cost} credits${total !== null ? ` · ${total} left` : ''}`}
                </span>
              )}

              {step === 1 && !outOfQuota && (
                <motion.button
                  whileTap={{ scale: prefersReduced ? 1 : 0.98 }}
                  onClick={openPicker}
                  className="btn btn--primary btn--sm"
                  aria-label="Add a new photo"
                >
                  <PlusIcon style={{ width: 14, height: 14 }} />
                  Add photo
                </motion.button>
              )}
            </div>
          </motion.div>

          <AnimatePresence mode="wait" initial={false}>
            {step === 1 && (
              <motion.section
                key="step1"
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="card card--bordered card--flat"
              >
                <motion.div variants={innerStagger} initial="hidden" animate="visible" className="stack-5">
                  <motion.div variants={innerItem} className="t-small t-mute">
                    Photos are analyzed for cosmetic, appearance-based insights only. This service does not provide medical advice or diagnosis.
                  </motion.div>
                  <motion.div variants={innerItem}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={outOfQuota ? undefined : openPicker}
                      onKeyDown={outOfQuota ? undefined : (e) => (e.key === 'Enter' || e.key === ' ') && openPicker()}
                      onDragOver={outOfQuota ? undefined : (e) => { e.preventDefault(); }}
                      onDrop={outOfQuota ? undefined : onDrop}
                      aria-disabled={outOfQuota ? 'true' : 'false'}
                      className={`drop-zone${outOfQuota ? ' drop-zone--disabled' : ''}`}
                      aria-label={outOfQuota ? 'Not enough credits' : 'Click to add a photo or drag & drop'}
                    >
                      {outOfQuota ? (
                        <div className="stack-4" style={{ alignItems: 'center' }}>
                          <div className="drop-zone__icon drop-zone__icon--coral">✕</div>
                          <div className="stack-2" style={{ textAlign: 'center' }}>
                            <p className="t-body" style={{ fontWeight: 500, color: 'var(--ink)', margin: 0 }}>
                              Not enough credits
                            </p>
                            <p className="t-small t-mute" style={{ maxWidth: '36ch', margin: 0 }}>
                              This scan costs {cost} credits. Top up or wait for your monthly reset.
                            </p>
                          </div>
                          <div className="row-3" style={{ flexWrap: 'wrap', justifyContent: 'center' }}>
                            <button
                              onClick={() => { navigateTo('/wallet'); }}
                              className="btn btn--primary btn--sm"
                            >
                              Buy credits
                            </button>
                            <button
                              onClick={() => { if (mode === 'modal') onCancel?.(); else navigateTo('/'); }}
                              className="btn btn--ghost btn--sm"
                            >
                              {mode === 'modal' ? 'Close' : 'Back'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="stack-3" style={{ alignItems: 'center' }}>
                          <div className="drop-zone__icon drop-zone__icon--teal">
                            <PlusIcon style={{ width: 24, height: 24 }} />
                          </div>
                          <div className="stack-2" style={{ textAlign: 'center' }}>
                            <p className="t-body" style={{ fontWeight: 500, color: 'var(--ink)', margin: 0 }}>
                              Click to add a photo
                            </p>
                            <p className="t-small t-mute" style={{ margin: 0 }}>
                              …or drag &amp; drop a JPG / PNG / WebP (up to 5&nbsp;MB)
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>

                  {!outOfQuota && (
                    <>
                      <motion.div
                        variants={innerItem}
                        className="sm:hidden flex items-center t-small t-mute select-none"
                        style={{ gap: 'var(--s-3)' }}
                      >
                        <span style={{ flex: 1, height: 1, background: 'var(--rule)' }} />
                        or
                        <span style={{ flex: 1, height: 1, background: 'var(--rule)' }} />
                      </motion.div>

                      <motion.div variants={innerItem} className="sm:hidden overflow-hidden flex justify-center">
                        <CameraCapture onCapture={handleBlob} />
                      </motion.div>
                    </>
                  )}
                </motion.div>
              </motion.section>
            )}

            {step === 2 && imageSrc && (
              <motion.section
                key="step2"
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="card card--bordered card--flat flex flex-col items-center"
              >
                {/* Library overrides: dark overlay is intentional for crop contrast */}
                <style>{`
                  .reactEasyCrop_CropArea { border: 1px solid rgba(255,255,255,0.25) !important; border-radius: var(--r-sm); box-shadow: 0 0 0 9999px rgba(0,0,0,0.85) !important; }
                  .reactEasyCrop_Container { background: var(--c-charcoal); }
                `}</style>

                <div className="w-full" style={{ maxWidth: 640 }}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{
                      opacity: 1, scale: 1,
                      boxShadow: faceStatus === 'Good'
                        ? ['var(--sh-deep)', '0 0 0 3px rgba(42,164,166,.55)', 'var(--sh-deep)']
                        : 'var(--sh-deep)',
                    }}
                    transition={faceStatus === 'Good'
                      ? { boxShadow: { duration: 0.6, ease: 'easeOut' }, ...spring }
                      : spring}
                    className="relative w-full aspect-square sm:aspect-video overflow-hidden"
                    style={{ borderRadius: 'var(--r-xl)', background: '#0a0a0a' }}
                  >
                    <Cropper
                      image={imageSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={1}
                      cropShape="rect"
                      showGrid={true}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                      zoomWithScroll
                      minZoom={1}
                      maxZoom={6}
                      restrictPosition
                    />
                    <ScannerFrame />

                    <div className="absolute inset-x-4 bottom-4 flex items-end justify-between pointer-events-none z-20">
                      <div className="stack-2">
                        <span className="hud-badge hud-badge--teal">Precision {zoom.toFixed(1)}x</span>
                        <span className="hud-badge hud-badge--dim">Standard Export · AI Ready</span>
                      </div>
                      <div className="hud-badge" style={{ width: 40, height: 40, justifyContent: 'center', padding: 0 }}>
                        <div style={{ width: 20, height: 20, border: '1.5px solid rgba(255,255,255,.2)', borderRadius: 'var(--r-xs)', display: 'grid', placeItems: 'center' }}>
                          <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--c-kivo-teal)' }} />
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="stack-6 flex flex-col items-center"
                    style={{ marginTop: 'var(--s-5)' }}
                  >
                    {/* Face detection status — always visible. The wrapper
                        reserves the badge's height so swapping between states
                        (checking / detected / not detected) never collapses the
                        slot and shifts the controls below it. */}
                    <div style={{ width: '100%', maxWidth: 480, minHeight: 'calc(2 * var(--s-3) + 1.5rem)', display: 'flex' }}>
                      <AnimatePresence mode="wait">
                        {faceStatus && (
                          <motion.div
                            key={faceStatus}
                            initial={{ opacity: 0, scale: 0.94, y: -6 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.96, y: 4 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 28 }}
                            style={{
                              width: '100%',
                              padding: 'var(--s-3) var(--s-4)',
                              borderRadius: 'var(--r-md)',
                              border: `1px solid ${checking ? 'var(--rule)' : faceStatus === 'Good' ? 'rgba(42,164,166,.35)' : 'rgba(244,168,142,.35)'}`,
                              background: checking ? 'var(--c-cream-weak)' : faceStatus === 'Good' ? 'rgba(42,164,166,.07)' : 'rgba(244,168,142,.07)',
                              display: 'flex', alignItems: 'center', gap: 'var(--s-3)',
                            }}
                          >
                            <span
                              style={{
                                width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                                background: checking ? 'var(--c-charcoal-60)' : faceStatus === 'Good' ? 'var(--c-kivo-teal)' : 'var(--c-coral)',
                              }}
                              className={checking ? 'animate-pulse' : ''}
                            />
                            <span className="t-small" style={{ fontWeight: 500, color: checking ? 'var(--ink-muted)' : faceStatus === 'Good' ? 'var(--c-deep-teal)' : 'var(--c-coral)' }}>
                              {checking ? 'Checking for a face…' : faceStatus === 'Good' ? 'Face detected — ready to analyse' : 'No face detected — adjust crop'}
                            </span>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    <div
                      className="card card--flat card--bordered row-4"
                      style={{ width: '100%', maxWidth: 480, padding: 'var(--s-4) var(--s-5)' }}
                    >
                      <span className="t-label">Zoom</span>
                      <div className="flex-1 relative flex items-center">
                        <input
                          type="range"
                          min={1}
                          max={6}
                          step={0.01}
                          value={zoom}
                          onChange={(e) => { setZoom(Number.parseFloat(e.target.value)); }}
                          className="w-full cursor-pointer"
                          style={{ accentColor: 'var(--c-kivo-teal)' }}
                        />
                      </div>
                      <span className="t-small" style={{ fontWeight: 700, minWidth: 32, color: 'var(--ink)' }}>
                        {zoom.toFixed(1)}x
                      </span>
                    </div>

                    <div style={{ display: 'flex', gap: 'var(--s-3)' }}>
                      <motion.button
                        whileTap={{ scale: prefersReduced ? 1 : 0.98 }}
                        onClick={() => { setStep(1); setImageSrc(null); setFaceStatus(''); setUploadError(null); }}
                        className="btn btn--ghost"
                        style={{ paddingInline: 'var(--s-6)' }}
                      >
                        Retake
                      </motion.button>
                      <motion.button
                        whileTap={{ scale: prefersReduced ? 1 : 0.98 }}
                        animate={!prefersReduced && faceStatus === 'Good' && !uploading ? { scale: [1, 1.04, 1] } : { scale: 1 }}
                        transition={{ duration: 0.4, ease: 'easeOut' }}
                        onClick={handleUploadJob}
                        disabled={outOfQuota || faceStatus !== 'Good' || uploading}
                        className="btn btn--primary"
                        style={{ paddingInline: 'var(--s-7)' }}
                      >
                        {uploading ? (
                          <div
                            className="animate-spin"
                            style={{ width: 20, height: 20, border: '2px solid rgba(248,244,238,.3)', borderTopColor: 'var(--c-cream)', borderRadius: '50%' }}
                          />
                        ) : (
                          'Analyse'
                        )}
                      </motion.button>
                    </div>

                    {uploadError && (
                      <motion.p
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="pill pill--coral t-small"
                        style={{ justifyContent: 'center', width: '100%', maxWidth: 480 }}
                      >
                        {uploadError}
                      </motion.p>
                    )}
                  </motion.div>
                </div>
              </motion.section>
            )}
          </AnimatePresence>
        </div>
      )}
    </>
  );

  if (mode === 'modal') {
    return (
      <ModalShell
        title={forcePremium ? 'Premium face scan' : 'Face scan'}
        onClose={() => onCancel?.()}
        widthClass="max-w-3xl"
      >
        <div style={{ padding: 'var(--s-5)' }}>{inner}</div>
      </ModalShell>
    );
  }

  return inner;
};

export default FaceScanScreen;
